import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Alert from '../../../../shared/components/Alert/Alert';
import Box from '../../../../shared/components/Box/Box';
import Loader from '../../../../shared/components/Loader/Loader';
import Typography from '../../../../shared/components/Typography/Typography';
import { getPartners } from '../../partners/partner.api';
import { getQuotes } from '../../../quotes/quote.api';
import { getAllEmployees } from '../../../hr/rh.api';
import { createAffair, getAffairs, transitionAffair } from '../affair.api';
import { affairFromApi } from '../affairModel';
import AffairCreationDialog from '../components/AffairCreationDialog';
import AffairDashboard from '../components/AffairDashboard';
import AffairKanban from '../components/AffairKanban';
import AffairTransitionDialog from '../components/AffairTransitionDialog';
import { getAssignableCrmUsers } from '../../crmUsers';

export default function AffairPipelinePage() {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || '';
  const stage = searchParams.get('stage') || '';
  const [affairs, setAffairs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [creationOpen, setCreationOpen] = useState(Boolean(companyId));
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [transitionDialog, setTransitionDialog] = useState(null);
  const [affairQuotes, setAffairQuotes] = useState([]);
  const [error, setError] = useState('');

  const loadAffairs = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await getAffairs({ ...(companyId ? { companyId } : {}), ...(stage ? { stage } : {}) });
      setAffairs((Array.isArray(rows) ? rows : []).map(affairFromApi));
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, stage]);

  useEffect(() => {
    loadAffairs();
    getPartners().then(setPartners).catch((requestError) => setError(requestError.message));
    getAllEmployees().then((rows) => setUsers(getAssignableCrmUsers(rows))).catch(() => setUsers([]));
  }, [loadAffairs]);

  const create = async (payload) => {
    try {
      setAction({ type: 'create' });
      await createAffair(payload);
      setCreationOpen(false);
      await loadAffairs();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setAction(null);
    }
  };

  const executeTransition = async (affair, payload) => {
    try {
      setAction({ type: 'transition', affairId: affair.id });
      await transitionAffair(affair.id, payload);
      setTransitionDialog(null);
      await loadAffairs();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setAction(null);
    }
  };

  const requestTransition = async (affair, toStage) => {
    if (!['won', 'lost', 'abandoned'].includes(toStage)) {
      await executeTransition(affair, { toStage });
      return;
    }

    if (toStage === 'won') {
      try {
        setAction({ type: 'load-quotes', affairId: affair.id });
        const quotes = await getQuotes();
        setAffairQuotes((Array.isArray(quotes) ? quotes : []).filter((quote) => String(quote.affairId || quote.affair_id || '') === String(affair.id)));
      } catch (requestError) {
        setError(requestError.message);
        return;
      } finally {
        setAction(null);
      }
    } else {
      setAffairQuotes([]);
    }
    setTransitionDialog({ affair, toStage });
  };

  return (
    <Box className="page-container module-page affair-pipeline-page">
      <header className="affair-pipeline-header">
        <div>
          <Typography variant="h4">Affaires</Typography>
          <Typography variant="body2" color="textSecondary">Suivi des affaires, de leur qualification jusqu’à leur issue et leur éventuelle transformation en projet ou intervention.</Typography>
        </div>
        <button className="basic-button active" type="button" onClick={() => setCreationOpen(true)}>Nouvelle affaire</button>
      </header>

      {error && <Alert severity="error" className="affair-pipeline-error">{error}</Alert>}
      {!loading && <AffairDashboard affairs={affairs} />}
      {loading ? <Loader label="Chargement du pipeline..." /> : (
        affairs.length > 0
          ? <AffairKanban affairs={affairs} action={action} onRequestTransition={requestTransition} />
          : <div className="affair-pipeline-empty"><h2>Aucune affaire</h2><p>Créez la première affaire de ce pipeline.</p></div>
      )}

      <AffairCreationDialog
        open={creationOpen}
        onClose={() => setCreationOpen(false)}
        onSubmit={create}
        loading={action?.type === 'create'}
        companyId={companyId}
        partners={partners}
        users={users}
      />
      <AffairTransitionDialog
        open={Boolean(transitionDialog)}
        affair={transitionDialog?.affair}
        toStage={transitionDialog?.toStage}
        quotes={affairQuotes}
        loading={action?.type === 'transition'}
        onClose={() => setTransitionDialog(null)}
        onSubmit={(payload) => executeTransition(transitionDialog.affair, payload)}
      />
    </Box>
  );
}
